import { useState, useCallback, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useWatchContractEvent, useReadContract, usePublicClient } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import { message } from 'antd';
import { api } from '../api/api';
import { CONTRACTS, ABIS } from '../contracts/config';
import { useNctBalance } from './useNctBalance';

interface DepositRequest {
  userAddress: string;
  amount: string;
  txHash: string;
  blockNumber: number;
  timestamp: number;
}

interface DepositResponse {
  success: boolean;
  data: {
    id: string;
    userAddress: string;
    amount: string;
    novaBalance: string;
    txHash: string;
    status: 'pending' | 'confirmed' | 'failed';
    createdAt: string;
  };
  message?: string;
}

interface WithdrawRequest {
  userAddress: string;
  amount: string;
  txHash: string;
  blockNumber: number;
  timestamp: number;
  feeAmount: string;
  netAmount: string;
}

interface WithdrawResponse {
  success: boolean;
  data: {
    id: string;
    userAddress: string;
    amount: string;
    feeAmount: string;
    netAmount: string;
    nctBalance: string;
    txHash: string;
    status: 'pending' | 'confirmed' | 'failed';
    createdAt: string;
  };
  message?: string;
}

interface WithdrawValidation {
  isValid: boolean;
  errors: string[];
  feeAmount: string;
  netAmount: string;
  minAmount: string;
}

interface NovaBalanceResponse {
  success: boolean;
  data: {
    address: string;
    balance: string;
    lastUpdated: string;
  };
  message?: string;
}

interface TransactionHistoryResponse {
  success: boolean;
  data: {
    transactions: Array<{
      id: string;
      type: 'deposit' | 'withdraw' | 'transfer';
      amount: string;
      txHash: string;
      status: 'pending' | 'confirmed' | 'failed';
      timestamp: string;
      blockNumber: number;
    }>;
    total: number;
    page: number;
    limit: number;
  };
  message?: string;
}

// 本地存储的待处理交易类型
interface PendingTransaction {
  id: string;
  userAddress: string;
  amount: string;
  txHash: string;
  blockNumber: number;
  timestamp: number;
  status: 'pending' | 'failed';
  type: 'deposit' | 'withdraw';
  feeAmount?: string;
  netAmount?: string;
}

interface BridgeState {
  isLoading: boolean;
  isDepositing: boolean;
  isWithdrawing: boolean;
  txHash: string | null;
  withdrawTxHash: string | null;
  error: string | null;
  lastDepositAmount: string | null;
  lastWithdrawAmount: string | null;
  isProcessingBackend: boolean;
  novaBalance: string;
  isNovaBalanceLoading: boolean;
  transactions: any[];
  isLoadingTransactions: boolean;
  pendingTransactions: PendingTransaction[];
  isTransactionInProgress: boolean;
}

interface UseBridgeReturn {
  state: BridgeState;
  nctBalance: string;
  nctAllowance: string;
  novaBalance: string;
  contractBalance: string;
  transactions: any[];
  minWithdrawAmount: string;
  withdrawFeeRate: string;
  depositNclabToNova: (amount: string) => Promise<boolean>;
  withdrawNovaToNclab: (amount: string) => Promise<boolean>;
  checkAllowance: (amount: string) => boolean;
  requestApproval: (amount: string) => Promise<boolean>;
  calculateWithdrawFee: (amount: string) => string;
  getNetWithdrawAmount: (amount: string) => string;
  validateWithdrawAmount: (amount: string) => { isValid: boolean; error: string | null };
  reset: () => void;
  refetchBalance: () => void;
  refetchAllowance: () => void;
  refetchNovaBalance: () => void;
  refetchContractBalance: () => void;
  refetchTransactions: () => void;
  retryFailedTransactions: () => Promise<void>;
}

export const useBridge = (): UseBridgeReturn => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  
  const [state, setState] = useState<BridgeState>({
    isLoading: false,
    isDepositing: false,
    isWithdrawing: false,
    txHash: null,
    withdrawTxHash: null,
    error: null,
    lastDepositAmount: null,
    lastWithdrawAmount: null,
    isProcessingBackend: false,
    novaBalance: "0",
    isNovaBalanceLoading: false,
    transactions: [],
    isLoadingTransactions: false,
    pendingTransactions: [],
    isTransactionInProgress: false,
  });

  // 使用统一的NCT余额和授权hook
  const { 
    balance: nctBalance, 
    isLoading: isNctBalanceLoading, 
    refetch: refetchBalance,
    getAllowance,
    checkAllowance: checkNctAllowance,
    approve: approveNct,
    needsApproval,
    isApproving,
    approveHash,
    isApproveConfirming,
    refetchAllowance: refetchNctAllowance,
  } = useNctBalance();

  // 获取购买管理合约的授权额度
  const nctAllowance = getAllowance(CONTRACTS.PurchaseManager || '');

  // 存款合约调用
  const {
    writeContract: depositContract,
    data: depositHash,
    reset: resetDeposit,
    error: depositError,
  } = useWriteContract();

  // 提款合约调用
  const {
    writeContract: withdrawContract,
    data: withdrawHash,
    reset: resetWithdraw,
    error: withdrawError,
  } = useWriteContract();

  // 等待存款交易确认
  const { 
    isLoading: isDepositConfirming,
    data: depositReceipt 
  } = useWaitForTransactionReceipt({
    hash: depositHash,
    query: {
      enabled: !!depositHash,
      refetchInterval: 500,
    },
  });

  // 等待提款交易确认
  const { 
    isLoading: isWithdrawConfirming,
    data: withdrawReceipt 
  } = useWaitForTransactionReceipt({
    hash: withdrawHash,
    query: {
      enabled: !!withdrawHash,
      refetchInterval: 500,
    },
  });

  // 读取用户在PurchaseManager合约中的余额
  const {
    data: userContractBalance,
    isLoading: isContractBalanceLoading,
    refetch: refetchContractBalance,
  } = useReadContract({
    address: CONTRACTS.PurchaseManager,
    abi: (ABIS.PURCHASE_MANAGER as any).abi,
    functionName: 'userDeposits',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && !!CONTRACTS.PurchaseManager,
      refetchInterval: 10000, // 每10秒检查一次
    },
  });

  // 读取最小提款限额
  const {
    data: minWithdrawAmount,
    isLoading: isMinWithdrawLoading,
    refetch: refetchMinWithdrawAmount,
  } = useReadContract({
    address: CONTRACTS.PurchaseManager,
    abi: (ABIS.PURCHASE_MANAGER as any).abi,
    functionName: 'minWithdrawAmount',
    query: {
      enabled: !!CONTRACTS.PurchaseManager,
      refetchInterval: 30000, // 每30秒检查一次
    },
  });

  // 读取提款手续费率
  const {
    data: withdrawFeeRate,
    isLoading: isWithdrawFeeLoading,
    refetch: refetchWithdrawFeeRate,
  } = useReadContract({
    address: CONTRACTS.PurchaseManager,
    abi: (ABIS.PURCHASE_MANAGER as any).abi,
    functionName: 'withdrawFeeRate',
    query: {
      enabled: !!CONTRACTS.PurchaseManager,
      refetchInterval: 30000, // 每30秒检查一次
    },
  });

  // 本地存储键
  const PENDING_TRANSACTIONS_KEY = `pending_transactions_${address}`;

  // 简化的本地存储管理器
  const storageManager = {
    // 保存待处理交易到本地存储
    savePendingTransaction: (transaction: Omit<PendingTransaction, 'id'>): string => {
      const id = `${transaction.txHash}_${Date.now()}`;
      const pendingTx: PendingTransaction = { ...transaction, id };
      
      const existing = storageManager.getPendingTransactions();
      const updated = [...existing.filter(tx => tx.txHash !== transaction.txHash), pendingTx];
      
      localStorage.setItem(PENDING_TRANSACTIONS_KEY, JSON.stringify(updated));
      return id;
    },

    // 获取待处理交易
    getPendingTransactions: (): PendingTransaction[] => {
      try {
        const stored = localStorage.getItem(PENDING_TRANSACTIONS_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        return [];
      }
    },

    // 移除已完成的交易
    removePendingTransaction: (txHash: string): void => {
      const existing = storageManager.getPendingTransactions();
      const updated = existing.filter(tx => tx.txHash !== txHash);
      localStorage.setItem(PENDING_TRANSACTIONS_KEY, JSON.stringify(updated));
    },

    // 清理过期的交易记录（超过24小时）
    cleanupExpiredTransactions: (): void => {
      try {
        const existing = storageManager.getPendingTransactions();
        const now = Date.now();
        const dayInMs = 24 * 60 * 60 * 1000; // 24小时
        
        const validTransactions = existing.filter(tx => {
          const txAge = now - tx.timestamp;
          return txAge < dayInMs; // 保留24小时内的交易
        });
        
        if (validTransactions.length !== existing.length) {
          localStorage.setItem(PENDING_TRANSACTIONS_KEY, JSON.stringify(validTransactions));
          console.log(`已清理 ${existing.length - validTransactions.length} 个过期交易记录`);
        }
      } catch (error) {
        console.error('清理过期交易记录失败:', error);
      }
    },

    // 完全清空待处理交易（紧急情况使用）
    clearAllPendingTransactions: (): void => {
      localStorage.removeItem(PENDING_TRANSACTIONS_KEY);
    },
  };

  // 安全的后端API服务
  const bridgeAPI = {
    // 处理存款请求 - 带重试机制
    async processDeposit(depositData: DepositRequest): Promise<DepositResponse> {
      const response = await api.post('/cryptokill/bridge/deposit', depositData, {
        timeout: 30000, // 普通30秒
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '后端处理失败');
      }
      
      return response.data;
    },

    // 查询NOVA余额 - 带重试机制
    async getNovaBalance(address: string): Promise<NovaBalanceResponse> {
      const response = await api.get(`/cryptokill/bridge/balance/${address}`, {
        timeout: 10000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '余额查询失败');
      }
      
      return response.data;
    },

    // 查询交易历史 - 带重试机制
    async getTransactionHistory(address: string, page = 1, limit = 20): Promise<TransactionHistoryResponse> {
      const response = await api.get(`/cryptokill/bridge/transactions/${address}?page=${page}&limit=${limit}`, {
        timeout: 15000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '交易历史查询失败');
      }
      
      return response.data;
    },

    // 验证交易状态 - 带重试机制
    async verifyTransaction(txHash: string): Promise<{ verified: boolean; status: string }> {
      const response = await api.post('/cryptokill/bridge/verify', { txHash }, {
        timeout: 15000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '交易验证失败');
      }
      
      return response.data;
    },

    // 获取待处理交易状态
    async getPendingTransactionStatus(txHash: string): Promise<{status: string; processed: boolean}> {
      const response = await api.get(`/cryptokill/bridge/status/${txHash}`, {
        timeout: 10000,
      });
      
      return response.data;
    },

    // 处理提款请求 - 带重试机制
    async processWithdraw(withdrawData: WithdrawRequest): Promise<WithdrawResponse> {
      const response = await api.post('/cryptokill/bridge/withdraw', withdrawData, {
        timeout: 30000, // 30秒超时
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '提款处理失败');
      }
      
      return response.data;
    },

    // 验证提款金额
    async validateWithdrawAmount(address: string, amount: string): Promise<WithdrawValidation> {
      const response = await api.post('/cryptokill/bridge/validate-withdraw', {
        address,
        amount,
      }, {
        timeout: 10000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '提款验证失败');
      }
      
      return response.data.data;
    },

    // 获取提款手续费信息
    async getWithdrawFee(amount: string): Promise<{feeAmount: string; netAmount: string}> {
      const response = await api.get(`/cryptokill/bridge/withdraw-fee?amount=${amount}`, {
        timeout: 10000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || '手续费查询失败');
      }
      
      return response.data.data;
    },
  };

  // 获取NOVA余额
  const fetchNovaBalance = useCallback(async () => {
    if (!address) return;
    
    setState(prev => ({ ...prev, isNovaBalanceLoading: true }));
    try {
      const response = await bridgeAPI.getNovaBalance(address);
      if (response.success) {
        setState(prev => ({ 
          ...prev, 
          novaBalance: response.data.balance, 
          isNovaBalanceLoading: false 
        }));
      } else {
        throw new Error(response.message || 'Failed to fetch NOVA balance');
      }
    } catch (error) {
      setState(prev => ({ ...prev, novaBalance: '0', isNovaBalanceLoading: false }));
    }
  }, [address]);

  // 监听合约余额变化
  useEffect(() => {
    if (userContractBalance !== undefined && userContractBalance !== null) {
      // 合约余额发生变化时，可以在这里添加必要的处理逻辑
      // 比如刷新NOVA余额等
    }
  }, [userContractBalance, address]);

  // 监听deposit事件
  useWatchContractEvent({
    address: CONTRACTS.PurchaseManager,
    abi: (ABIS.PURCHASE_MANAGER as any).abi,
    eventName: 'Deposit',
    args: address ? [address] : undefined,
    onLogs(logs) {
      logs.forEach(async (log) => {
        try {
          const logWithArgs = log as any;
          if (logWithArgs.args && logWithArgs.args.amount && address) {
            const eventUser = logWithArgs.args.user;
            const amount = formatEther(logWithArgs.args.amount as bigint);
            
            // 确保事件来自当前用户
            if (eventUser && eventUser.toLowerCase() !== address.toLowerCase()) {
              return;
            }

            setState(prev => ({
              ...prev,
              lastDepositAmount: amount,
            }));

            // 刷新余额和合约状态
            setTimeout(() => {
              refetchBalance();
              refetchAllowance();
              refetchContractBalance();
            }, 1000);
          }
        } catch (error) {
          // 静默处理错误
        }
      });
    },
    enabled: !!address && !!CONTRACTS.PurchaseManager,
  });

  // 监听withdraw事件
  useWatchContractEvent({
    address: CONTRACTS.PurchaseManager,
    abi: (ABIS.PURCHASE_MANAGER as any).abi,
    eventName: 'Withdraw',
    args: address ? [address] : undefined,
    onLogs(logs) {
      logs.forEach(async (log) => {
        try {
          const logWithArgs = log as any;
          if (logWithArgs.args && logWithArgs.args.amount && address) {
            const eventUser = logWithArgs.args.user;
            const amount = formatEther(logWithArgs.args.amount as bigint);
            const fee = logWithArgs.args.fee ? formatEther(logWithArgs.args.fee as bigint) : '0';
            
            // 确保事件来自当前用户
            if (eventUser && eventUser.toLowerCase() !== address.toLowerCase()) {
              return;
            }

            setState(prev => ({
              ...prev,
              lastWithdrawAmount: amount,
            }));

            // 刷新余额和合约状态
            setTimeout(() => {
              refetchBalance();
              refetchContractBalance();
              fetchNovaBalance();
            }, 1000);
          }
        } catch (error) {
          // 静默处理错误
        }
      });
    },
    enabled: !!address && !!CONTRACTS.PurchaseManager,
  });

  // 检查是否有足够的授权额度
  const checkAllowance = useCallback((amount: string): boolean => {
    return checkNctAllowance(CONTRACTS.PurchaseManager || '', amount);
  }, [checkNctAllowance]);

  // 请求代币授权
  const requestApproval = useCallback(async (amount: string): Promise<boolean> => {
    if (!address || !CONTRACTS.PurchaseManager) {
      message.error('钱包未连接或合约地址未配置');
      return false;
    }

    try {
      setState(prev => ({ ...prev, error: null }));
      
      const success = await approveNct(CONTRACTS.PurchaseManager, amount);
      return success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '授权失败，请重试';
      message.error(errorMessage);
      setState(prev => ({ ...prev, error: errorMessage }));
      return false;
    }
  }, [address, approveNct]);

  // 获取交易历史
  const fetchTransactionHistory = useCallback(async () => {
    if (!address) return;
    
    try {
      setState(prev => ({ ...prev, isLoadingTransactions: true }));
      const response = await bridgeAPI.getTransactionHistory(address);
      
      setState(prev => ({
        ...prev,
        transactions: response.data.transactions,
        isLoadingTransactions: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        transactions: [],
        isLoadingTransactions: false,
      }));
    }
  }, [address]);

  // 计算提款手续费
  const calculateWithdrawFee = useCallback((amount: string): string => {
    if (!withdrawFeeRate || !amount) return '0';
    
    const amountBigInt = parseEther(amount);
    const feeRate = withdrawFeeRate as bigint;
    const feeAmount = (amountBigInt * feeRate) / BigInt(10000); // 基点转换
    
    return formatEther(feeAmount);
  }, [withdrawFeeRate]);

  // 获取实际到账金额
  const getNetWithdrawAmount = useCallback((amount: string): string => {
    if (!amount) return '0';
    
    const feeAmount = calculateWithdrawFee(amount);
    const netAmount = parseFloat(amount) - parseFloat(feeAmount);
    
    return Math.max(0, netAmount).toString();
  }, [calculateWithdrawFee]);

  // 验证提款金额
  const validateWithdrawAmount = useCallback((amount: string): { isValid: boolean; error: string | null } => {
    if (!amount || parseFloat(amount) <= 0) {
      return { isValid: false, error: '请输入有效的提款金额' };
    }

    const withdrawAmount = parseFloat(amount);
    const currentNovaBalance = parseFloat(state.novaBalance || '0');
    
    // 检查最小提款限制
    if (minWithdrawAmount) {
      const minAmount = parseFloat(formatEther(minWithdrawAmount as bigint));
      if (withdrawAmount < minAmount) {
        return { isValid: false, error: `最小提款金额为 ${minAmount.toLocaleString()} NOVA` };
      }
    }
    
    // 检查余额
    if (withdrawAmount > currentNovaBalance) {
      return { isValid: false, error: `提款金额不能超过当前余额 ${currentNovaBalance.toLocaleString()} NOVA` };
    }

    return { isValid: true, error: null };
  }, [state.novaBalance, minWithdrawAmount]);

  // 存款函数 - 分离授权和存款逻辑，移除Promise等待
  const depositNclabToNova = useCallback(async (amount: string): Promise<boolean> => {
    if (!address) {
      message.error('请先连接钱包');
      return false;
    }

    if (state.isTransactionInProgress) {
      message.warning('已有交易在进行中，请等待完成');
      return false;
    }

    // 验证金额限制 - 移除最小金额限制
    const depositAmount = parseFloat(amount);
    const currentBalance = parseFloat(nctBalance || '0');
    
    if (depositAmount > currentBalance) {
      message.error('存款金额不能超过当前余额');
      return false;
    }

    try {
      // 1. 首先检查授权状态
      if (!checkAllowance(amount)) {
        // 需要授权 - 只执行授权操作
        setState(prev => ({ 
          ...prev, 
          error: null,
          isTransactionInProgress: true 
        }));
        
        const approved = await requestApproval(amount);
        
        setState(prev => ({ 
          ...prev, 
          isTransactionInProgress: false 
        }));
        
        if (approved) {
          return false; // 返回false，表示需要用户再次点击
        } else {
          setState(prev => ({ 
            ...prev, 
            error: '授权失败'
          }));
          return false;
        }
      }

      // 2. 已授权，执行存款交易
      setState(prev => ({ 
        ...prev, 
        isDepositing: true, 
        error: null,
        isTransactionInProgress: true 
      }));
      
      const amountWei = parseEther(amount);

      // 3. 提交存款交易
      depositContract({
        address: CONTRACTS.PurchaseManager as `0x${string}`,
        abi: (ABIS.PURCHASE_MANAGER as any).abi,
        functionName: "deposit" as const,
        args: [amountWei],
      });

      // 保存当前存款信息，用于后续处理
      setState(prev => ({ 
        ...prev, 
        lastDepositAmount: amount,
        isProcessingBackend: false
      }));

      return true; // 立即返回，不等待确认

    } catch (error: any) {
      let errorMessage = '存款失败';
      if (error.message?.includes('User rejected')) {
        errorMessage = '用户取消交易';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = '余额不足';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      setState(prev => ({ 
        ...prev, 
        isDepositing: false,
        isProcessingBackend: false,
        isTransactionInProgress: false,
        error: errorMessage
      }));
      
      return false;
    }
  }, [address, checkAllowance, requestApproval, depositContract, state.isTransactionInProgress, nctBalance]);

  // NOVA -> NClab 提款函数（软错误处理）
  const withdrawNovaToNclab = useCallback(async (amount: string): Promise<boolean> => {
    if (!address) {
      message.error('请先连接钱包');
      return false;
    }

    if (state.isTransactionInProgress) {
      message.warning('已有交易在进行中，请等待完成');
      return false;
    }

    // 验证金额
    const validation = validateWithdrawAmount(amount);
    if (!validation.isValid) {
      message.error(validation.error || '提款金额无效');
      return false;
    }

    try {
      setState(prev => ({ 
        ...prev, 
        isWithdrawing: true,
        isTransactionInProgress: true,
        error: null,
        lastWithdrawAmount: amount,
      }));

      // 计算手续费和实际到账金额
      const feeAmount = calculateWithdrawFee(amount);
      const netAmount = getNetWithdrawAmount(amount);

      // 调用合约提款函数
      const amountWei = parseEther(amount);

      withdrawContract({
        address: CONTRACTS.PurchaseManager as `0x${string}`,
        abi: (ABIS.PURCHASE_MANAGER as any).abi,
        functionName: "withdraw" as const,
        args: [amountWei],
      });

      return true;

    } catch (error: any) {
      let errorMessage = '提款失败';
      if (error.message?.includes('User rejected')) {
        errorMessage = '用户取消交易';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = '余额不足';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      setState(prev => ({ 
        ...prev, 
        isWithdrawing: false,
        isTransactionInProgress: false,
        error: errorMessage
      }));
      
      return false;
    }
  }, [address, validateWithdrawAmount, calculateWithdrawFee, getNetWithdrawAmount, withdrawContract, state.isTransactionInProgress]);

  // 重置状态
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isDepositing: false,
      isWithdrawing: false,
      txHash: null,
      withdrawTxHash: null,
      error: null,
      lastDepositAmount: null,
      lastWithdrawAmount: null,
      isProcessingBackend: false,
      novaBalance: "0",
      isNovaBalanceLoading: false,
      transactions: [],
      isLoadingTransactions: false,
      pendingTransactions: [],
      isTransactionInProgress: false,
    });
    resetDeposit();
    resetWithdraw();
  }, [resetDeposit, resetWithdraw]);

  // 刷新NOVA余额
  const refetchNovaBalance = useCallback(() => {
    fetchNovaBalance();
  }, [fetchNovaBalance]);

  // 刷新授权额度
  const refetchAllowance = useCallback(() => {
    refetchNctAllowance(CONTRACTS.PurchaseManager || '');
  }, [refetchNctAllowance]);

  // 初始化时获取NOVA余额并重置状态
  useEffect(() => {
    if (address) {
      fetchNovaBalance();
      // 清理过期的交易记录
      storageManager.cleanupExpiredTransactions();
    } else {
      setState({
        isLoading: false,
        isDepositing: false,
        isWithdrawing: false,
        txHash: null,
        withdrawTxHash: null,
        error: null,
        lastDepositAmount: null,
        lastWithdrawAmount: null,
        isProcessingBackend: false,
        novaBalance: "0",
        isNovaBalanceLoading: false,
        transactions: [],
        isLoadingTransactions: false,
        pendingTransactions: [],
        isTransactionInProgress: false,
      });
      resetDeposit();
    }
  }, [address, fetchNovaBalance, resetDeposit]);

  // 监听存款交易提交
  useEffect(() => {
    if (depositHash && isDepositConfirming) {
      setState(prev => ({ 
        ...prev, 
        txHash: depositHash,
        isDepositing: true 
      }));
    }
  }, [depositHash, isDepositConfirming]);

  // 监听授权交易状态变化
  useEffect(() => {
    if (approveHash && !isApproveConfirming) {
      setTimeout(() => {
        refetchAllowance();
        refetchBalance();
      }, 500);
    }
  }, [approveHash]);

  // 监听授权交易提交
  useEffect(() => {
    if (approveHash && isApproveConfirming) {
      // 静默处理授权交易提交
    }
  }, [approveHash, isApproveConfirming]);

  // 监听存款交易错误
  useEffect(() => {
    if (depositError && state.isTransactionInProgress) {
      message.error('存款交易失败');
      setState(prev => ({ 
        ...prev, 
        isDepositing: false,
        isTransactionInProgress: false,
        error: '存款交易失败'
      }));
    }
  }, [depositError, state.isTransactionInProgress]);

  // 监听提款交易提交
  useEffect(() => {
    if (withdrawHash && isWithdrawConfirming) {
      setState(prev => ({ 
        ...prev, 
        withdrawTxHash: withdrawHash,
        isWithdrawing: true 
      }));
    }
  }, [withdrawHash, isWithdrawConfirming]);

  // 监听提款交易错误
  useEffect(() => {
    if (withdrawError && state.isTransactionInProgress) {
      message.error('提款交易失败');
      setState(prev => ({ 
        ...prev, 
        isWithdrawing: false,
        isTransactionInProgress: false,
        error: '提款交易失败'
      }));
    }
  }, [withdrawError, state.isTransactionInProgress]);

  // 监听存款交易确认并处理后端逻辑
  useEffect(() => {
    const handleDepositConfirmation = async () => {
      // 确保有交易hash、交易已确认、不在确认中、有存款金额
      // 移除isTransactionInProgress检查，因为可能在某些情况下被意外重置
      if (!depositHash || !depositReceipt || isDepositConfirming || !state.lastDepositAmount) {
        return;
      }

      // 如果已经在处理后端或者交易已经成功完成，跳过
      if (state.isProcessingBackend) {
        return;
      }

      try {
        if (depositReceipt.status === 'success') {
          message.success('存款确认成功！');
          
          setState(prev => ({ 
            ...prev, 
            txHash: depositHash,
            isProcessingBackend: true 
          }));

          // 准备后端请求数据
          const depositRequest: DepositRequest = {
            userAddress: address!,
            amount: state.lastDepositAmount,
            txHash: depositHash,
            blockNumber: Number(depositReceipt.blockNumber),
            timestamp: Date.now(),
          };

          // 保存到待处理列表（防止后端处理失败）
          storageManager.savePendingTransaction({
            userAddress: address!,
            amount: state.lastDepositAmount,
            txHash: depositHash,
            blockNumber: Number(depositReceipt.blockNumber),
            timestamp: Date.now(),
            status: 'pending',
            type: 'deposit',
          });

          // 尝试处理后端
          try {
            const response = await bridgeAPI.processDeposit(depositRequest);
            
            if (response.success) {
              // 移除待处理记录
              storageManager.removePendingTransaction(depositHash);
              
              // 更新状态
              setState(prev => ({
                ...prev,
                isProcessingBackend: false,
                isDepositing: false,
                isTransactionInProgress: false,
              }));
              
              // 刷新相关状态
              refetchBalance();
              await fetchNovaBalance();
              fetchTransactionHistory();
              
            } else {
              throw new Error(response.message || '后端处理失败');
            }
            
          } catch (backendError) {
            // 设置为失败状态，保留在重试队列中
            storageManager.savePendingTransaction({
              userAddress: address!,
              amount: state.lastDepositAmount,
              txHash: depositHash,
              blockNumber: Number(depositReceipt.blockNumber),
              timestamp: Date.now(),
              status: 'failed',
              type: 'deposit',
            });
            
            setState(prev => ({
              ...prev,
              isProcessingBackend: false,
              isDepositing: false,
              isTransactionInProgress: false,
              error: '处理似乎失败，请手动重试'
            }));
          }
          
        } else {
          // 交易失败
          message.error('区块链交易失败');
          setState(prev => ({ 
            ...prev, 
            isDepositing: false,
            isProcessingBackend: false,
            isTransactionInProgress: false,
            error: '区块链交易失败'
          }));
        }
        
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          isDepositing: false,
          isProcessingBackend: false,
          isTransactionInProgress: false,
          error: '处理交易确认时出错'
        }));
      }
    };

    handleDepositConfirmation();
  }, [depositHash, depositReceipt, isDepositConfirming, state.lastDepositAmount, address, fetchNovaBalance, fetchTransactionHistory, refetchBalance]);

  // 监听提款交易确认并处理后端逻辑
  useEffect(() => {
    const handleWithdrawConfirmation = async () => {
      // 确保有交易hash、交易已确认、不在确认中、有提款金额
      // 移除isTransactionInProgress检查，因为可能在某些情况下被意外重置
      if (!withdrawHash || !withdrawReceipt || isWithdrawConfirming || !state.lastWithdrawAmount) {
        return;
      }

      // 如果已经在处理后端或者交易已经成功完成，跳过
      if (state.isProcessingBackend) {
        return;
      }

      try {
        if (withdrawReceipt.status === 'success') {
          message.success('提款确认成功！');
          
          setState(prev => ({ 
            ...prev, 
            withdrawTxHash: withdrawHash,
            isProcessingBackend: true 
          }));

          // 计算手续费和实际到账金额
          const feeAmount = calculateWithdrawFee(state.lastWithdrawAmount);
          const netAmount = getNetWithdrawAmount(state.lastWithdrawAmount);

          // 准备后端请求数据
          const withdrawRequest: WithdrawRequest = {
            userAddress: address!,
            amount: state.lastWithdrawAmount,
            txHash: withdrawHash,
            blockNumber: Number(withdrawReceipt.blockNumber),
            timestamp: Date.now(),
            feeAmount,
            netAmount,
          };

          // 保存到待处理列表（防止后端处理失败）
          storageManager.savePendingTransaction({
            userAddress: address!,
            amount: state.lastWithdrawAmount,
            txHash: withdrawHash,
            blockNumber: Number(withdrawReceipt.blockNumber),
            timestamp: Date.now(),
            status: 'pending',
            type: 'withdraw',
            feeAmount,
            netAmount,
          });

          // 尝试处理后端
          try {
            const response = await bridgeAPI.processWithdraw(withdrawRequest);
            
            if (response.success) {
              message.success('提款成功！NClab余额已更新');
              
              // 移除待处理记录
              storageManager.removePendingTransaction(withdrawHash);
              
              // 更新状态
              setState(prev => ({
                ...prev,
                isProcessingBackend: false,
                isWithdrawing: false,
                isTransactionInProgress: false,
              }));
              
              // 刷新相关状态
              refetchBalance();
              await fetchNovaBalance();
              fetchTransactionHistory();
              
            } else {
              throw new Error(response.message || '后端处理失败');
            }
            
          } catch (backendError) {
            // 设置为失败状态，保留在重试队列中
            storageManager.savePendingTransaction({
              userAddress: address!,
              amount: state.lastWithdrawAmount,
              txHash: withdrawHash,
              blockNumber: Number(withdrawReceipt.blockNumber),
              timestamp: Date.now(),
              status: 'failed',
              type: 'withdraw',
              feeAmount,
              netAmount,
            });
            
            setState(prev => ({
              ...prev,
              isProcessingBackend: false,
              isWithdrawing: false,
              isTransactionInProgress: false,
              error: '处理似乎失败，请手动重试'
            }));
          }
          
        } else {
          // 交易失败
          message.error('区块链交易失败');
          setState(prev => ({ 
            ...prev, 
            isWithdrawing: false,
            isProcessingBackend: false,
            isTransactionInProgress: false,
            error: '区块链交易失败'
          }));
        }
        
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          isWithdrawing: false,
          isProcessingBackend: false,
          isTransactionInProgress: false,
          error: '处理交易确认时出错'
        }));
      }
    };

    handleWithdrawConfirmation();
  }, [withdrawHash, withdrawReceipt, isWithdrawConfirming, state.lastWithdrawAmount, address, calculateWithdrawFee, getNetWithdrawAmount, fetchNovaBalance, fetchTransactionHistory, refetchBalance]);

  // 计算最终的loading状态
  const finalIsLoading = (isApproveConfirming && !!approveHash) || 
                        (isDepositConfirming && !!depositHash) || 
                        state.isProcessingBackend ||
                        (isApproving && !!approveHash);

  // 主动重试失败的交易
  const retryFailedTransactions = useCallback(async () => {
    if (!address) return;
    
    try {
      const pendingTxs = storageManager.getPendingTransactions()
        .filter(tx => tx.status === 'failed');
      
      if (pendingTxs.length === 0) {
        message.warning('没有需要重试的交易');
        return;
      }
      
      message.loading(`正在重试 ${pendingTxs.length} 个失败的交易...`, 0);
      
      for (const pendingTx of pendingTxs) {
        try {
          storageManager.removePendingTransaction(pendingTx.txHash);
          
          if (pendingTx.type === 'deposit') {
            const depositRequest: DepositRequest = {
              userAddress: pendingTx.userAddress,
              amount: pendingTx.amount,
              txHash: pendingTx.txHash,
              blockNumber: pendingTx.blockNumber,
              timestamp: pendingTx.timestamp,
            };
            
            const response = await bridgeAPI.processDeposit(depositRequest);
            
          } else if (pendingTx.type === 'withdraw') {
            const withdrawRequest: WithdrawRequest = {
              userAddress: pendingTx.userAddress,
              amount: pendingTx.amount,
              txHash: pendingTx.txHash,
              blockNumber: pendingTx.blockNumber,
              timestamp: pendingTx.timestamp,
              feeAmount: pendingTx.feeAmount || '0',
              netAmount: pendingTx.netAmount || pendingTx.amount,
            };
            
            const response = await bridgeAPI.processWithdraw(withdrawRequest);
            
            if (response.success) {
              message.success('提款重试成功！NClab余额已更新');
            }
          }
          
        } catch (error) {
          storageManager.savePendingTransaction({
            userAddress: pendingTx.userAddress,
            amount: pendingTx.amount,
            txHash: pendingTx.txHash,
            blockNumber: pendingTx.blockNumber,
            timestamp: pendingTx.timestamp,
            status: 'failed',
            type: pendingTx.type,
            feeAmount: pendingTx.feeAmount,
            netAmount: pendingTx.netAmount,
          });
        }
      }
      
      message.destroy();
      message.success('重试完成');
      await fetchNovaBalance();
      
    } catch (error) {
      message.destroy();
      message.error('重试过程中发生错误');
    }
  }, [address, fetchNovaBalance]);

  // 初始化时获取交易历史
  useEffect(() => {
    if (address) {
      fetchTransactionHistory();
    }
  }, [address, fetchTransactionHistory]);

  // 更新待处理交易状态到state
  useEffect(() => {
    if (address) {
      const pendingTxs = storageManager.getPendingTransactions();
      setState(prev => ({
        ...prev,
        pendingTransactions: pendingTxs,
      }));
    }
  }, [address, state.txHash]); // 当有新交易时更新

  // 交易超时重置机制
  useEffect(() => {
    if (state.isTransactionInProgress && !state.isProcessingBackend && !isDepositConfirming && !isWithdrawConfirming) {
      // 只有在交易进行中、没有在处理后端、且没有在等待确认时，才设置超时
      const timeout = setTimeout(() => {
        setState(prev => ({ 
          ...prev, 
          isTransactionInProgress: false,
          isDepositing: false,
          isWithdrawing: false,
          error: '交易超时，请重试'
        }));
        message.warning('交易超时，请重试');
      }, 30000); // 30秒超时

      return () => clearTimeout(timeout);
    }
  }, [state.isTransactionInProgress, state.isProcessingBackend, isDepositConfirming, isWithdrawConfirming]);

  return {
    state: {
      isLoading: finalIsLoading,
      isDepositing: isDepositConfirming && !!depositHash,
      isWithdrawing: isWithdrawConfirming && !!withdrawHash,
      txHash: depositHash || approveHash || null,
      withdrawTxHash: withdrawHash || null,
      error: state.error,
      lastDepositAmount: state.lastDepositAmount,
      lastWithdrawAmount: state.lastWithdrawAmount,
      isProcessingBackend: state.isProcessingBackend,
      novaBalance: state.novaBalance,
      isNovaBalanceLoading: state.isNovaBalanceLoading,
      transactions: state.transactions,
      isLoadingTransactions: state.isLoadingTransactions,
      pendingTransactions: state.pendingTransactions,
      isTransactionInProgress: state.isTransactionInProgress,
    },
    nctBalance,
    nctAllowance,
    novaBalance: state.novaBalance,
    contractBalance: userContractBalance ? formatEther(userContractBalance as bigint) : "0",
    transactions: state.transactions,
    minWithdrawAmount: minWithdrawAmount ? formatEther(minWithdrawAmount as bigint) : "0",
    withdrawFeeRate: withdrawFeeRate ? (Number(withdrawFeeRate) / 10000).toString() : "0",
    depositNclabToNova,
    withdrawNovaToNclab,
    checkAllowance,
    requestApproval,
    calculateWithdrawFee,
    getNetWithdrawAmount,
    validateWithdrawAmount,
    reset,
    refetchBalance,
    refetchAllowance,
    refetchNovaBalance: fetchNovaBalance,
    refetchContractBalance,
    refetchTransactions: fetchTransactionHistory,
    retryFailedTransactions,
  };
}; 